/* eslint-disable camelcase */
import isEqual from "lodash-es/isEqual";
import isUndefined from "lodash-es/isUndefined";
import omitBy from "lodash-es/omitBy";
import { clearBeamStorage, logger, memoizeLast } from "../../utils";
import { initNetworkListeners, teardownNetworkListeners } from "../../utils/network-listeners";
import {
  BeamCartChangeEvent,
  BeamCartCreatedEvent,
  BeamNetworkCallEvent,
  BeamNonprofitSelectEvent,
  BeamNonprofitSelectionRemovedEvent,
  BeamWidgetInitialNonprofitSyncCancelEvent,
  BeamWidgetInitialNonprofitSyncEvent,
} from "../../utils/events";
import { createScopedLocalStorage } from "../../utils/local-storage";
import { BEAM_CART_COOKIE_NAME, TBeamCartLocalStorage, TCart, TCartItems } from "../../shared/cart-contents";
import { BeamCartAPIConfig, updateCart } from "../cart";
import { WIDGET_NAMES } from "../../api-sdk/constants";
import { getBeamCartId, getExternalCartId } from "../../utils/cart";

export type BeamShopifyCartIntegrationConfig = BeamCartAPIConfig & {
  storeId: number; // will be added to cart custom_attributes, and used in cart API call
  chainId?: number; // will be added to cart custom_attributes
};

type ShopifyDiscountApplication = {
  title: string;
  value: string;
};

type ShopifyLineLevelDiscountAllocations = {
  amount?: string;
  discount_application: ShopifyDiscountApplication;
};

type ShopifyCartBeamAttribute = {
  nonprofit_id?: number | null;
  selection_id?: string;
  beam_cart_id?: string;
  shopify_cart_id?: string;
  chain_id?: number;
  store_id?: number;
  show_beam?: boolean;
  remote_session_id?: string;
};

const SHOPIFY_BASE_URL = window.Shopify?.routes?.root || "/"; // see window.d.ts

let cartIntegrationAbortController: AbortController | null = null; // Set when registration starts; aborting removes its listeners

let allowEmitInitialNonprofitSync = true; // Skip updating local storage if already run initially
let skipEmitInitialNonprofitSync = false; // Allow other processes to skip the nonprofit sync from cart attributes

const SHOPIFY_CART_COOKIE_NAME = "cart";

/**
 * @main registerCartIntegration - set up event listeners to integrate Beam with Shopify cart
 * @return {boolean} returns true if NEW listeners were created, false if setup was already done
 */
async function registerCartIntegration(
  config: BeamShopifyCartIntegrationConfig,
  autoUpdateShopifyCartAttributes = true,
) {
  if (cartIntegrationAbortController) {
    return false; // Do nothing if already registered, or a registration attempt is already in progress
  }

  // All listeners below are registered with this signal so that
  // unregisterCartIntegration can remove them all with a single abort()
  const abortController = new AbortController();
  cartIntegrationAbortController = abortController;
  const { signal } = abortController;

  // logger.debug("[cart-page][registerCartIntegration] initializing network listeners");
  // Set up event listeners for AJAX events (idempotent; re-enables dispatching if previously torn down)
  initNetworkListeners();

  /**
   * Allow another component to cancel the loading & setting
   * of the Shopify cart selection (like if a selection is made before we've gotten the previous values)
   */
  window.addEventListener(
    BeamWidgetInitialNonprofitSyncCancelEvent.eventName,
    async () => {
      // logger.debug("[cart-page][BeamWidgetInitialNonprofitSyncCancelEvent]");
      skipEmitInitialNonprofitSync = true;
    },
    { signal },
  );

  // When any network call happens, check if it's for the Shopify /cart AJAX Api.
  // If so, and the cart is updated, send the cart to Beam
  window.addEventListener(
    BeamNetworkCallEvent.eventName,
    async (_event: Event) => {
      const event = _event as BeamNetworkCallEvent;
      const cartChangePaths = /cart\/(add|change|update|clear)/;
      // note: response urls below follow redirects
      const requestUrlStr = event.detail.type === "xhr" ? event.detail.xhr.responseURL : event.detail.response.url;
      const url = new URL(requestUrlStr);
      if (!cartChangePaths.test(url.pathname)) {
        return;
      }
      // logger.debug("[cart-page][BeamNetworkCallEvent] getting current cart");
      const currentCart = await getCurrentCart(config, signal);
      // Recheck: unregister can't cancel an invocation already in flight
      if (signal.aborted || !currentCart.changed) {
        // logger.debug(`[cart-page][BeamNetworkCallEvent] cart has not changed from URL ${requestUrlStr}`, currentCart.cart);
        return;
      }
      // Send cart to Beam to update timestamp, which is used for cart abandonment / ROI calc
      // logger.debug(`[cart-page][BeamNetworkCallEvent] cart has changed from URL ${requestUrlStr}`, currentCart.cart);
      updateCart(config, currentCart.cart);
    },
    { signal },
  );

  // Listen to Beam's own events to integrate Beam data into Shopify order
  let lastNonprofitId: number | null | undefined;
  let lastSelectionId: string | null | undefined;
  window.addEventListener(
    BeamNonprofitSelectEvent.eventName,
    async (_event) => {
      const event = _event as BeamNonprofitSelectEvent;
      logger.debug("[cart-page][BeamNonprofitSelectEvent]", event.detail);
      const { source, selectedNonprofitId, selectionId } = event.detail;

      // On nonprofit selection, we always write Beam's attributes
      // to the Shopify cart attributes
      if (source === WIDGET_NAMES.select_nonprofit) {
        // Skip update if nothing changed (can happen if widget is re-initialized and
        // emits event after cache restoration, i.e., on cart re-render).
        if (lastNonprofitId === selectedNonprofitId && lastSelectionId === selectionId) {
          return;
        }

        const beamCartData = await getBeamAttributesForCart(config);
        await appendBeamAttributesToCart({
          ...beamCartData,
          selectedNonprofitId,
          selectionId,
        });
        lastSelectionId = selectionId;
        lastNonprofitId = selectedNonprofitId;
      }
    },
    { signal },
  );

  /**
   * Upon removal of the selected nonprofit from the widget and local storage,
   * an event is emitted and that event is then used to update the cart attributes accordingly
   * This event listener updates the cart attributes based on the emitted event
   */
  window.addEventListener(
    BeamNonprofitSelectionRemovedEvent.eventName,
    async (_event) => {
      const event = _event as BeamNonprofitSelectionRemovedEvent;
      logger.debug("[cart-page][BeamNonprofitSelectionRemovedEvent]", event.detail);
      const { newNonprofitId, selectionId } = event.detail;

      if (lastNonprofitId === newNonprofitId && lastSelectionId === selectionId) {
        return;
      }

      // On nonprofit deselection, we always write Beam's attributes
      // to the Shopify cart attributes
      const beamCartData = await getBeamAttributesForCart(config);
      await appendBeamAttributesToCart({
        ...beamCartData,
        selectedNonprofitId: null,
        selectionId,
      });
      lastSelectionId = selectionId;
      lastNonprofitId = newNonprofitId;
    },
    { signal },
  );

  /**
   * When a BeamCart is created, write beam metadata to the Shopify cart if
   * we need to capture it for Statsig or Extensibility
   */
  window.addEventListener(
    BeamCartCreatedEvent.eventName,
    async (_event: Event) => {
      const event = _event as BeamCartCreatedEvent;
      logger.debug("[cart-page][BeamCartCreatedEvent]", event.detail);
      if (window.statsig || autoUpdateShopifyCartAttributes) {
        const beamCartData = await getBeamAttributesForCart(config);
        await appendBeamAttributesToCart(beamCartData);
      }
    },
    { signal },
  );

  /**
   * On any cart changes, write beam metadata to the Shopify cart if
   * we need to capture it for Statsig or Extensibility
   */
  window.addEventListener(
    BeamCartChangeEvent.eventName,
    async (_event: Event) => {
      const event = _event as BeamCartChangeEvent;
      logger.debug("[cart-page][BeamCartChangeEvent]", event.detail);
      if (window.statsig || autoUpdateShopifyCartAttributes) {
        const beamCartData = await getBeamAttributesForCart(config);
        await appendBeamAttributesToCart(beamCartData);
      }
    },
    { signal },
  );

  try {
    // Handle full-page load, ie, first-time page load and reloads from form submit events
    // This creates a GET to /cart.js
    if (signal.aborted) {
      return false; // unregistered before we even started
    }
    const currentCart = await getCurrentCart(config, signal);
    if (signal.aborted) {
      return false; // unregistered while the GET was in flight
    }
    // logger.debug("[cart-page][registerCartIntegration] initial cart fetch result", currentCart);
    await updateCart(config, currentCart.cart, true);
    if (signal.aborted) {
      return false; // unregistered while updateCart was in flight
    }

    return true;
  } catch (err) {
    // Undo on failure so a later call can retry instead of being stuck
    if (cartIntegrationAbortController === abortController) {
      abortController.abort();
      cartIntegrationAbortController = null;
    }
    throw err;
  }
}

/**
 * @main unregisterCartIntegration - tear down listeners set up by registerCartIntegration.
 * Pass { clearStorage: true } to also wipe Beam's localStorage/cookies in the same call
 * (clearBeamStorage from src/utils, which is also callable on its own — for cases that want
 * storage cleared without touching listeners, or independently of this integration entirely).
 * @return {boolean} true if this page load's listeners were torn down, false if
 * registerCartIntegration never ran this session (storage still clears if requested, either way)
 */
async function unregisterCartIntegration(
  config: BeamShopifyCartIntegrationConfig,
  { clearStorage = false }: { clearStorage?: boolean } = {},
) {
  // Also covers an unregister call that arrives mid-registration, before this is set true
  const wasRegisteredThisSession = Boolean(cartIntegrationAbortController);

  if (wasRegisteredThisSession) {
    // 1. Remove ALL event listeners registered with this controller's signal
    cartIntegrationAbortController?.abort();
    cartIntegrationAbortController = null;

    // 2. Stop fetch/XHR hooks from dispatching (patches stay installed, just inert)
    teardownNetworkListeners();

    // 3. Drop memoized state so nothing stale survives a re-register
    appendBeamAttributesToCart.reset();
    addBeamAttributesToCart.reset();

    // 4. Reset module flags so a future opt-in can re-register cleanly
    allowEmitInitialNonprofitSync = true;
    skipEmitInitialNonprofitSync = false;
  }

  // 5. Optionally clear storage too. Runs regardless of wasRegisteredThisSession — that state
  // is persisted browser-side, not tied to whether a registration happened this page load.
  if (clearStorage) {
    clearBeamStorage(config);
  }

  return wasRegisteredThisSession;
}

/* ***************************************** HELPER FUNCTIONS *****************************************
   These may be used individually to integrate with carts if the registerCartIntegration
   script doesn't work for some reason.
   ****************************************************************************************************/

async function appendToShopifyCartBeamAttribute(config: BeamShopifyCartIntegrationConfig) {
  const beamCartData = await getBeamAttributesForCart(config);
  await appendBeamAttributesToCart(beamCartData);
}

/**
 * Gathers the base Beam data needed for updating a Shopify cart's metadata
 */
async function getBeamAttributesForCart(config: BeamShopifyCartIntegrationConfig) {
  let showBeam;
  let remoteSessionId;

  if (window.statsig) {
    // If the site is using Statsig, but not for Beam, we need to ignore it
    try {
      const statsig = window.statsig as any;
      const statsigLayerProperty = statsig?.getLayer("beam_trial_layer")?.get("show_beam");
      if (typeof statsigLayerProperty === "boolean") {
        showBeam = statsig.getLayer("beam_trial_layer").get("show_beam", false /* default to hide */) as boolean;
        remoteSessionId = statsig.getStableID();
      }
    } catch (err) {
      // logger.error(err)
    }
  }

  const cartId = getExternalCartId(SHOPIFY_CART_COOKIE_NAME, config);
  const beamCartId = getBeamCartId(BEAM_CART_COOKIE_NAME, config);
  const { chainId, storeId } = config;

  return {
    showBeam: showBeam ?? undefined,
    remoteSessionId: remoteSessionId ?? undefined,
    beamCartId: beamCartId && beamCartId !== "null" ? beamCartId : undefined,
    cartId: cartId && cartId !== "null" ? cartId : undefined,
    chainId,
    storeId,
  };
}

/**
 * Adds Statsig data to a Shopify cart
 */
const appendStatsigToShopifyCartAttributes = appendToShopifyCartBeamAttribute;

/**
 * Detects change in cart since last page load,
 * by calling GET /cart.js, and returns cart values.
 * Used for:
 *  - Form-based carts where the page refreshes to modify cart instead of using AJAX calls
 *  - Hydrogen/GraphQL based carts (needs to be integrated manually)
 */
async function getCurrentCart(
  config: BeamShopifyCartIntegrationConfig,
  signal?: AbortSignal,
): Promise<{
  changed: boolean;
  cart: {
    cartId: string;
    subtotal: number;
    itemCount: number;
    currencyCode: string;
  } & TCart;
}> {
  const {
    token, // equal to "cart" cookie
    total_price, // this is the pretax value, after discounts, and multiplied by 100 (integer from decimal)
    item_count,
    currency,
    items,
    cart_level_discount_applications,
    attributes,
  } = await getShopifyCart();

  let changed = false;

  const localStorage = createScopedLocalStorage(config);

  // The cart cache is used to detect changes after page refreshes / across tabs
  const cachedCart: TBeamCartLocalStorage | null = localStorage.getItemJson("cart");
  // The Beam cart cookie is used to identify the cart across subdomains
  // Use explicit null if empty because API requires it
  const beamCartId: string | null = getBeamCartId(BEAM_CART_COOKIE_NAME, config);

  const cart = {
    cartId: token,
    beamCartId,
    subtotal: total_price / 100,
    itemCount: item_count,
    currencyCode: currency,
    schema: { source: "generic" } as const,
    content: {
      // pull out minimal fields from full line items
      items: (items as TCartItems).map(({ sku, final_line_price, product_id, line_level_discount_allocations }) => {
        const remoteProductIdentifier = sku || String(product_id);
        const localAmount = final_line_price / 100;
        const discounts = (line_level_discount_allocations as ShopifyLineLevelDiscountAllocations[])?.map(
          ({ discount_application }) => {
            return { code: discount_application?.title, applicable: true };
          },
        );
        return { remoteProductIdentifier, localAmount, discounts };
      }),
      discounts: (cart_level_discount_applications as ShopifyDiscountApplication[]).map(({ title }) => {
        return { code: title, applicable: true };
      }),
    },
  };

  let beamAttr: ShopifyCartBeamAttribute | null = null;

  try {
    if (attributes?.beam) {
      beamAttr = JSON.parse(attributes.beam);
    }
  } catch (err) {
    logger.error(err);
  }

  /**
   * Only attempt to emit BeamWidgetInitialNonprofitSyncEvent once
   * as soon as we see a "beam" attribute for the first time.
   * allowEmitInitialNonprofitSync is local to this file to keep this
   * from running more than once.
   * skipEmitInitialNonprofitSync is settable by an event listener
   * so other processes can stop this sync from happening.
   * signal is checked too, so a call from a since-torn-down registration doesn't consume
   * the one-shot flag on its behalf.
   */
  if (beamAttr && allowEmitInitialNonprofitSync && !skipEmitInitialNonprofitSync && !signal?.aborted) {
    allowEmitInitialNonprofitSync = false;

    const { nonprofit_id, selection_id } = beamAttr;

    const storedNonprofit = localStorage.getItem("nonprofit");
    const storedSelection = localStorage.getItem("transaction");

    const nonprofitChanged = nonprofit_id !== undefined && String(nonprofit_id) !== storedNonprofit;
    const selectionChanged = selection_id != null && String(selection_id) !== storedSelection;

    // Only dispatch if something changed, and if it's not skipped
    if (nonprofitChanged || selectionChanged) {
      if (nonprofitChanged) {
        localStorage.setItem("nonprofit", String(nonprofit_id));
        localStorage.setItem("nonprofit_selected_at", new Date().toISOString());
      }

      if (selectionChanged) {
        localStorage.setItem("transaction", String(selection_id));
        localStorage.setItem("nonprofit_selected_at", new Date().toISOString());
      }

      window.dispatchEvent(
        new BeamWidgetInitialNonprofitSyncEvent({
          nonprofitId: nonprofit_id,
          nonprofitIdChanged: nonprofitChanged,
          selectionId: selection_id,
          selectionIdChanged: selectionChanged,
        }),
      );
    }
  }

  if (
    !isEqual(cart, {
      ...cachedCart,
      beamCartId: cachedCart?.beamCartId,
    })
  ) {
    changed = true;
  }

  // logger.debug("[cart-page][getCurrentCart] has cart changed?", changed, cart);

  return { changed, cart };
}

/**
 * trackCart - Backwards-compatible alias for updateCart
 */
const trackCart = updateCart;

/**
 * Helper function to get the Shopify cart attributes
 */
const getShopifyCart = async function () {
  const cart = await window
    .fetch(SHOPIFY_BASE_URL + "cart.js", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
    .then((res) => res.json());
  return cart;
};

/**
 * Sends Beam data to Shopify to integrate with order as custom attributes.
 * Memoized so that calls with the same data as the previous don't create additional API calls to Shopify.
 * This will do an append, not a full overwrite of the attributes in the beam property
 */
const appendBeamAttributesToCart = memoizeLast(async function appendBeamAttributesToCart({
  selectedNonprofitId,
  selectionId,
  beamCartId,
  cartId,
  chainId,
  storeId,
  showBeam,
  remoteSessionId,
}: {
  selectedNonprofitId?: number | null;
  selectionId?: string;
  beamCartId?: string;
  cartId?: string;
  chainId?: number;
  storeId?: number;
  showBeam?: boolean;
  remoteSessionId?: string;
}) {
  // logger.debug("[cart-page][appendBeamAttributesToCart] fetching existing cart");
  const { attributes } = await getShopifyCart();

  // Get only the Beam attribute, if it exists
  const existingBeamCartAttrString = attributes?.beam;
  let existingBeamCartAttr = {};
  try {
    if (existingBeamCartAttrString) {
      existingBeamCartAttr = JSON.parse(existingBeamCartAttrString);
    }
  } catch (err) {
    logger.error(err);
  }

  const newBeamAttrs = omitBy(
    {
      nonprofit_id: selectedNonprofitId,
      selection_id: selectionId,
      beam_cart_id: beamCartId,
      shopify_cart_id: cartId,
      chain_id: chainId,
      store_id: storeId,
      show_beam: showBeam,
      remote_session_id: remoteSessionId,
    },
    isUndefined,
  );

  const appendedBeamAttributes = {
    ...existingBeamCartAttr,
    ...newBeamAttrs,
  } as ShopifyCartBeamAttribute;

  // For the nonprofitId, if we specifically get a null value here,
  // we want to remove it from the cart attributes, not just write "null"
  if (selectedNonprofitId === null) {
    appendedBeamAttributes.nonprofit_id = undefined;
  }

  const beamCartAttrs = {
    beam: JSON.stringify(appendedBeamAttributes),
  };

  // If the attributes match, no reason to update the cart
  if (isEqual(appendedBeamAttributes, existingBeamCartAttr)) {
    // logger.debug("[cart-page][appendBeamAttributesToCart] cart attributes match, skipping update");
    return;
  }

  try {
    // logger.debug("[cart-page][appendBeamAttributesToCart] updating Shopify cart");
    await window.fetch(SHOPIFY_BASE_URL + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: beamCartAttrs }),
    });
  } catch (err) {
    logger.error(err); // don't throw, allow later calls to continue
  }
});

/**
 * Sends Beam data to Shopify to integrate with order as custom attributes.
 * Memoized so that calls with the same data as the previous don't create additional API calls to Shopify.
 * This method force-updates the "beam" attribute with the values passed in
 */
const addBeamAttributesToCart = memoizeLast(async function addBeamAttributesToCart({
  selectedNonprofitId,
  selectionId,
  beamCartId,
  cartId,
  chainId,
  storeId,
}: {
  selectedNonprofitId?: number | null;
  selectionId?: string;
  beamCartId?: string;
  cartId?: string;
  chainId?: number;
  storeId?: number;
}) {
  let showBeam;
  let remoteSessionId;

  if (window.statsig) {
    // If the site is using Statsig, but not for Beam, we need to ignore it
    try {
      const statsig = window.statsig as any;
      const statsigLayerProperty = statsig?.getLayer("beam_trial_layer")?.get("show_beam");
      if (typeof statsigLayerProperty === "boolean") {
        showBeam = statsig.getLayer("beam_trial_layer").get("show_beam", false /* default to hide */) as boolean;
        remoteSessionId = statsig.getStableID();
      }
    } catch (err) {
      // logger.error(err)
    }
  }

  const allBeamAttributes = omitBy(
    {
      nonprofit_id: selectedNonprofitId,
      selection_id: selectionId,
      beam_cart_id: beamCartId,
      shopify_cart_id: cartId,
      chain_id: chainId,
      store_id: storeId,
      show_beam: showBeam,
      remote_session_id: remoteSessionId,
    },
    isUndefined,
  ) as ShopifyCartBeamAttribute;

  // For the nonprofitId, if we specifically get a null value here,
  // we want to remove it from the cart attributes, not just write "null"
  if (selectedNonprofitId === null) {
    allBeamAttributes.nonprofit_id = undefined;
  }

  const beamCartAttrs = {
    beam: JSON.stringify(allBeamAttributes),
  };

  try {
    await window.fetch(SHOPIFY_BASE_URL + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: beamCartAttrs }),
    });
  } catch (err) {
    logger.error(err); // don't throw, allow later calls to continue
  }
});

declare global {
  interface Window {
    statsig?: any; // attached by Statsig CDN script
    Shopify?: any;
  }
}

export {
  trackCart,
  getCurrentCart,
  addBeamAttributesToCart,
  unregisterCartIntegration,
  appendStatsigToShopifyCartAttributes,
  appendToShopifyCartBeamAttribute,
  registerCartIntegration,
  appendBeamAttributesToCart,
  getShopifyCart,
  getBeamAttributesForCart,
};
